import { arrayify } from '@sonar-software/sonar-ts-lib';
import { DynamicQueryParams } from '@sonar-software/sonar-ts-types';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { UpdateQueryParams } from '@hooks';

import {
	AccessExplainerDetails,
	AccessExplainerObjectDetails,
	AccessObject,
	AccessType
} from '@sonar-software/sonar-core';
import { useCallback, useMemo, useRef, useState } from 'react';

export type AccessComparisonQueryParams = DynamicQueryParams & SortByFilterQueryParams & {
	accessId?: Array<string>;
	accessType?: Array<AccessType>;
};

export type AccessComparisonObjectQueryParams = AccessComparisonQueryParams & {
	objectName?: string;
};

type ComparisonQueryParams = AccessComparisonQueryParams | AccessComparisonObjectQueryParams;

// Custom hook for filtering the API response data to only include access objects that match the current
// query parameters. This allows column removal without requiring a new API call by
// filtering out columns that were removed from the URL parameters.
export const useFilteredAccessComparisonData = <
	T extends AccessExplainerDetails | AccessExplainerObjectDetails
>(
		data: T | null | undefined,
		queryParams: ComparisonQueryParams,
		filterType: 'objects' | 'fields'
	) => useMemo(() => {
		if (!data) {
			return data;
		}

		const currentAccessIds = arrayify(queryParams.accessId ?? []);
		const currentAccessTypes = arrayify(queryParams.accessType ?? []);

		// Filter accesses based on matching IDs and types
		const filteredAccesses: Record<string, AccessObject> = {};
		currentAccessIds.forEach((accessId, idx) => {
			const accessType = currentAccessTypes[ idx ];
			const accessObj = data.accesses[ accessId ];

			if (accessObj && accessObj.type === accessType) {
				filteredAccesses[ accessId ] = accessObj;
			}
		});

		const filterPermissions = (permissions: Record<string, string>) =>
			Object.fromEntries(
				Object.entries(permissions).filter(([ accessId ]) =>
					currentAccessIds.includes(accessId))
			);

		if (filterType === 'objects' && 'objects' in data) {
			return {
				...data,
				accesses: filteredAccesses,
				objects: data.objects.map((obj) => ({
					...obj,
					permissions: filterPermissions(obj.permissions)
				}))
			};
		}

		if (filterType === 'fields' && 'fields' in data) {
			return {
				...data,
				accesses: filteredAccesses,
				fields: data.fields.map((field) => ({
					...field,
					permissions: filterPermissions(field.permissions)
				}))
			};
		}

		return data;
	}, [ data, queryParams.accessId, queryParams.accessType, filterType ]);

export const removeAccessSelectionById =
(accessIds: Array<string>, accessTypes: Array<AccessType>, targetKey: string) => {
	const idx = accessIds.indexOf(targetKey);
	if (idx === -1) {
		return null;
	}

	return {
		updatedIds: [ ...accessIds.slice(0, idx), ...accessIds.slice(idx + 1) ],
		updatedTypes: [ ...accessTypes.slice(0, idx), ...accessTypes.slice(idx + 1) ]
	};
};

export const useAddComparison = () => {
	const additionalColumnsRef = useRef<Array<AccessObject & { id?: string }>>([]);
	const isRemovingColumn = useRef(false);
	const isTriggeredByRefresh = useRef(false);
	const [ hasNewColumns, setHasNewColumns ] = useState(false);

	const handleAdditionalColumnsChange = useCallback((columns: Array<AccessObject>) => {
		additionalColumnsRef.current = columns;
		setHasNewColumns(columns.length > 0);
	}, []);

	const resetColumnRefs = useCallback(() => {
		additionalColumnsRef.current = [];
		setHasNewColumns(false);
	}, []);

	const prepareApiParams = useCallback((
		baseIds: Array<string>,
		baseTypes: Array<AccessType>
	) => {
		const shouldIncludeExtraColumns = !isRemovingColumn.current && isTriggeredByRefresh.current;
		const extraColumns = shouldIncludeExtraColumns ? additionalColumnsRef.current : [];
		const extraIds: Array<string> = extraColumns.map((col) => col.id || col.name).filter(Boolean);
		const extraTypes: Array<AccessType> = extraColumns.map((col) => col.type).filter(Boolean);

		const accessIdList = extraColumns.length ? [ ...baseIds, ...extraIds ] : baseIds;
		const accessTypeList = extraColumns.length ? [ ...baseTypes, ...extraTypes ] : baseTypes;

		return {
			accessIdList,
			accessTypeList,
			extraColumns
		};
	}, []);

	const createRemoveColumnHandler = useCallback((
		queryParams: ComparisonQueryParams,
		updateUrl: (params: ComparisonQueryParams) => void,
		updateParams: UpdateQueryParams<ComparisonQueryParams>
	) => (accessKey: string) => {
		const currentAccessIds = arrayify(queryParams.accessId ?? []);
		const currentAccessTypes = arrayify(queryParams.accessType ?? []);

		const remainingAccessSelection = removeAccessSelectionById(currentAccessIds, currentAccessTypes, accessKey);

		if (!remainingAccessSelection) {
			return;
		}

		const { updatedIds, updatedTypes } = remainingAccessSelection;

		isRemovingColumn.current = true;

		const urlParams: ComparisonQueryParams = {
			...queryParams,
			accessId: updatedIds,
			accessType: updatedTypes,
			filters: queryParams.filters ?? '',
			ordering: queryParams.ordering ?? ''
		};

		updateUrl(urlParams);

		updateParams({
			accessId: updatedIds,
			accessType: updatedTypes
		});
	}, []);

	const createForceRefreshHandler = useCallback((
		callApi: (params?: ComparisonQueryParams) => void,
		queryParams: ComparisonQueryParams
	) => () => {
		isRemovingColumn.current = false;
		isTriggeredByRefresh.current = true;
		callApi(queryParams);
	}, []);

	return {
		additionalColumnsRef,
		createForceRefreshHandler,
		createRemoveColumnHandler,
		handleAdditionalColumnsChange,
		hasNewColumns,
		isRemovingColumn,
		isTriggeredByRefresh,
		prepareApiParams,
		resetColumnRefs,
		setHasNewColumns
	};
};
